import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingOverlay } from '@/components/ui';

type OrgFeatureName = 'qaAutomation' | 'esimLpacProvisioning';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'support';
  requiredPlan?: string[];
  /**
   * Restrict the route to organizations whose active org has this feature
   * flag set to `true` (`organizations.settings.features.<name> === true`).
   * Super admins bypass the check (they need to access every tenant's QA
   * surfaces during debugging).
   *
   * Mirror of the backend's `isQaAutomationTenant(org)` helper. Used by
   * /admin/qa-automation.
   */
  requireOrgFeature?: OrgFeatureName;
}

export function ProtectedRoute({ children, requiredRole, requiredPlan, requireOrgFeature }: ProtectedRouteProps) {
  const { currentUser, loading, systemRole, isSuperAdmin, activeOrganization } = useAuth();
  const location = useLocation();

  // CRITICAL: Always show loading while auth is initializing
  if (loading) {
    return <LoadingOverlay isLoading={true} message="Loading..." />;
  }

  // CRITICAL: If no user after loading completes, redirect to login
  // This ensures we NEVER show protected content without authentication
  if (!currentUser) {
    console.log('[ProtectedRoute] No user found, redirecting to login');
    // Redirect to login page but save the attempted URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Role-based access control: redirect to home if user lacks required role
  if (requiredRole === 'super_admin' && systemRole !== 'super_admin') {
    console.log('[ProtectedRoute] User lacks super_admin role, redirecting to home');
    return <Navigate to="/" replace />;
  }
  if (requiredRole === 'support' && systemRole !== 'super_admin' && systemRole !== 'support') {
    console.log('[ProtectedRoute] User lacks support role, redirecting to home');
    return <Navigate to="/" replace />;
  }

  // Plan-based access control: redirect to home if org plan doesn't qualify.
  // Super admins bypass this check (they can view-as any org).
  if (requiredPlan && !isSuperAdmin) {
    const plan = activeOrganization?.subscription?.plan ?? '';
    if (!requiredPlan.includes(plan)) {
      console.log('[ProtectedRoute] Plan insufficient, redirecting to home');
      return <Navigate to="/" replace />;
    }
  }

  // Per-tenant feature flag gate. Super-admins bypass; everyone else needs
  // the active org's settings.features.<name> to be explicitly true.
  if (requireOrgFeature && systemRole !== 'super_admin') {
    const enabled = activeOrganization?.settings?.features?.[requireOrgFeature] === true;
    if (!enabled) {
      console.log(`[ProtectedRoute] Active org lacks "${requireOrgFeature}" feature, redirecting to home`);
      return <Navigate to="/" replace />;
    }
  }

  // User is authenticated and authorized, show protected content
  return <>{children}</>;
}

